import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { mergeSiteSettings, SITE_SETTINGS_DEFAULTS } from './siteSettingsDefaults';
import { applyJewelryProfilesFromSettings } from './jewelry/jewelryProfileRegistry';
import { setStoreOptionLists } from './jewelry/jewelryStoreOptionLists';

const SiteSettingsContext = createContext({
  settings: SITE_SETTINGS_DEFAULTS,
  status: 'loading',
  refresh: () => {},
});

export { SiteSettingsContext };

const SETTINGS_CACHE_KEY = 'saloni_site_settings_cache_v1';
const SETTINGS_CACHE_TTL_MS = 5 * 60 * 1000;
const SETTINGS_RECENT_TTL_MS = 4000;
let inFlightSettings = null;
let recentSettings = null;

function readSettingsCache() {
  try {
    const raw = sessionStorage.getItem(SETTINGS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.ts || Date.now() - parsed.ts > SETTINGS_CACHE_TTL_MS) return null;
    if (!parsed.settings || typeof parsed.settings !== 'object') return null;
    return parsed.settings;
  } catch {
    return null;
  }
}

function writeSettingsCache(settings) {
  try {
    sessionStorage.setItem(
      SETTINGS_CACHE_KEY,
      JSON.stringify({ ts: Date.now(), settings }),
    );
  } catch {
    /* quota / private mode */
  }
}

function applySettingsSideEffects(merged) {
  applyJewelryProfilesFromSettings(merged);
  const jewelryConfig = merged?.jewelry_profiles_config;
  const storeListsRaw =
    jewelryConfig?.storeOptionLists && typeof jewelryConfig.storeOptionLists === 'object'
      ? { storeOptionLists: jewelryConfig.storeOptionLists }
      : jewelryConfig?.storeOptionDefaults && typeof jewelryConfig.storeOptionDefaults === 'object'
        ? jewelryConfig.storeOptionDefaults
        : null;
  setStoreOptionLists(storeListsRaw);
}

async function fetchSiteSettings({ force = false } = {}) {
  if (!force && inFlightSettings) return inFlightSettings;
  if (!force && recentSettings && Date.now() - recentSettings.ts < SETTINGS_RECENT_TTL_MS) {
    return recentSettings.payload;
  }
  if (force) {
    recentSettings = null;
  }

  const promise = (async () => {
    // No cache-bust query: allows StrictMode / multi-hook coalescing.
    const r = await fetch('/api/settings.php', { cache: 'no-store' });
    const data = await r.json().catch(() => ({}));
    if (data.ok && data.settings) {
      const merged = mergeSiteSettings(data.settings);
      recentSettings = { ts: Date.now(), payload: merged };
      return merged;
    }
    return null;
  })();

  inFlightSettings = promise;
  promise.finally(() => {
    if (inFlightSettings === promise) {
      inFlightSettings = null;
    }
  });
  return promise;
}

export function SiteSettingsProvider({ children }) {
  const bootCachedRef = useRef(null);
  if (bootCachedRef.current === null) {
    bootCachedRef.current = readSettingsCache();
  }
  const bootCached = bootCachedRef.current;

  const [settings, setSettings] = useState(() => {
    if (bootCached) {
      applySettingsSideEffects(bootCached);
      return bootCached;
    }
    return SITE_SETTINGS_DEFAULTS;
  });
  const [status, setStatus] = useState(bootCached ? 'ready' : 'loading');

  const load = React.useCallback(async ({ silent = false, force = false } = {}) => {
    if (!silent) setStatus((prev) => (prev === 'ready' ? prev : 'loading'));
    try {
      const merged = await fetchSiteSettings({ force });
      if (merged) {
        applySettingsSideEffects(merged);
        setSettings(merged);
        writeSettingsCache(merged);
        setStatus('ready');
      } else {
        setSettings(SITE_SETTINGS_DEFAULTS);
        setStatus('error');
      }
    } catch {
      setSettings(SITE_SETTINGS_DEFAULTS);
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    load({ silent: Boolean(bootCached) });
  }, [load, bootCached]);

  const value = useMemo(
    () => ({ settings, status, refresh: () => load({ silent: false, force: true }) }),
    [settings, status, load],
  );

  return <SiteSettingsContext.Provider value={value}>{children}</SiteSettingsContext.Provider>;
}

export function useSiteSettings() {
  return useContext(SiteSettingsContext);
}
